import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { ImportBatch, ImportRow } from '@/api/journalImport.api';
import { cn } from '@/utils/cn';

/**
 * The staged rows of an import batch, grouped into the journals they will become (GAP-017).
 *
 * Grouped rather than listed flat because "does this balance?" is a question about a journal, not
 * a row — and an unbalanced group is the most common reason an import stalls. A flat list makes
 * the operator do the grouping in their head to find out which journal is short.
 */

interface Props {
  batch: ImportBatch;
}

interface Group {
  key:      string;
  rows:     ImportRow[];
  debit:    number;
  credit:   number;
  hasError: boolean;
}

const money = (n: number) =>
  n === 0 ? '' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const JournalImportReviewTable = ({ batch }: Props) => {
  const groups: Group[] = [];
  const index = new Map<string, Group>();

  for (const row of [...batch.rows].sort((a, b) => a.rowNumber - b.rowNumber)) {
    let group = index.get(row.journalGroup);
    if (!group) {
      group = { key: row.journalGroup, rows: [], debit: 0, credit: 0, hasError: false };
      index.set(row.journalGroup, group);
      groups.push(group);
    }
    group.rows.push(row);
    group.debit  += row.debitAmount;
    group.credit += row.creditAmount;
    if (row.validationStatus === 'INVALID') group.hasError = true;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-gray-400">
            <th className="px-3 py-2 font-semibold w-16">Row</th>
            <th className="px-3 py-2 font-semibold">Account</th>
            <th className="px-3 py-2 font-semibold">Narration</th>
            <th className="px-3 py-2 font-semibold text-right w-32">Debit</th>
            <th className="px-3 py-2 font-semibold text-right w-32">Credit</th>
            <th className="px-3 py-2 font-semibold">Status</th>
          </tr>
        </thead>

        {groups.map(group => {
          // Compared to the cent, not with a tolerance: a journal that is out by anything is out.
          const balanced = Math.abs(group.debit - group.credit) < 0.005;

          return (
            <tbody key={group.key} className="divide-y divide-slate-100 dark:divide-slate-800">
              <tr className="bg-slate-50 dark:bg-slate-800/50">
                <td colSpan={3} className="px-3 py-2 font-medium text-slate-700 dark:text-slate-200">
                  Journal {group.key}
                  <span className="ml-2 text-xs font-normal text-slate-500 dark:text-gray-400">
                    {group.rows.length} line{group.rows.length === 1 ? '' : 's'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-700 dark:text-slate-200">
                  {money(group.debit)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-700 dark:text-slate-200">
                  {money(group.credit)}
                </td>
                <td className="px-3 py-2">
                  {!balanced ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 dark:text-rose-400">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Out by {Math.abs(group.debit - group.credit).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  ) : group.hasError ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="w-3.5 h-3.5" /> Has errors
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Balanced
                    </span>
                  )}
                </td>
              </tr>

              {group.rows.map(row => (
                <tr key={row.id}
                    className={cn(
                      'align-top',
                      row.validationStatus === 'INVALID'
                        ? 'bg-amber-50/70 dark:bg-amber-900/15'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700/30')}>
                  <td className="px-3 py-2 text-slate-400 tabular-nums">{row.rowNumber}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700 dark:text-slate-200">
                    {row.accountCode ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                    {row.narration ?? '—'}
                    {/* The message sits under the line it is about — the operator is looking at
                        the row, not at a list of errors somewhere else on the page. */}
                    {row.validationMessage && (
                      <div className="mt-0.5 text-xs text-amber-800 dark:text-amber-300">
                        {row.validationMessage}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                    {money(row.debitAmount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                    {money(row.creditAmount)}
                  </td>
                  <td className="px-3 py-2">
                    {row.wasPosted ? (
                      <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">Posted</span>
                    ) : row.postedJournalId ? (
                      <span className="text-xs font-medium text-sky-700 dark:text-sky-400">Submitted</span>
                    ) : row.validationStatus === 'INVALID' ? (
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Error</span>
                    ) : row.validationStatus === 'VALID' ? (
                      <span className="text-xs font-medium text-slate-500 dark:text-gray-400">Ready</span>
                    ) : (
                      <span className="text-xs text-slate-400">Not checked</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
};
